import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Settings2, User, Globe, Link, Trash2, RefreshCw, Copy, AlertTriangle, RotateCcw, Loader2 } from 'lucide-react';
import { useScreenData } from '@/hooks/useScreenData';
import { useUser } from '@/contexts/UserContext';
import { useTimezone } from '@/hooks/use-timezone';
import { toast } from 'sonner';
import { TIMEZONES } from '@/lib/timezones';
import { useState, useEffect } from 'react';
import { calendarSyncApi } from '@/services/api';
import { DataModeToggle } from '@/components/DataModeToggle';
import { useAudit } from '@/contexts/AuditContext';

export function UserSetup() {
  const { settings, updateSettings, currentMode } = useScreenData('settings');
  const { user } = useUser();
  const audit = useAudit();
  const {
    timezone: effectiveTimezone,
    setUserTimezone,
    clearUserTimezone,
    hasUserOverride,
    systemDefault,
  } = useTimezone();

  const [activeTab, setActiveTab] = useState('defaults');

  // Calendar sync state
  const [calendarUrl, setCalendarUrl] = useState<string>('');
  const [isGeneratingUrl, setIsGeneratingUrl] = useState(false);
  const [isDeletingUrl, setIsDeletingUrl] = useState(false);
  const [isLoadingCalendarUrl, setIsLoadingCalendarUrl] = useState(false);
  const [isDeleteUrlDialogOpen, setIsDeleteUrlDialogOpen] = useState(false);

  const timezoneOptions = TIMEZONES.map(tz => ({
    value: tz.value,
    label: tz.label,
  }));

  // Normalize legacy timeline view values
  const normalizeTimelineView = (value: string | undefined): string => {
    if (!value) return 'resource';
    const normalized = value.toLowerCase();
    if (normalized === 'customer') return 'customer';
    if (normalized === 'project & resource' || normalized === 'project-resource') return 'project-resource';
    if (normalized === 'resource') return 'resource';
    if (normalized === 'role & resource' || normalized === 'role-resource') return 'role-resource';
    return 'resource';
  };

  // Fetch existing calendar URL on mount
  useEffect(() => {
    const fetchCalendarUrl = async () => {
      if (!user?.id) return;
      setIsLoadingCalendarUrl(true);
      try {
        const response = await calendarSyncApi.generateLink({ userId: user.id });
        const url = response.icsFileUrl;
        setCalendarUrl(url && url.trim() !== '' ? url : '');
      } catch {
        setCalendarUrl('');
      } finally {
        setIsLoadingCalendarUrl(false);
      }
    };
    fetchCalendarUrl();
  }, [user?.id]);

  // ── Defaults handlers ──────────────────────────────────────────────────────
  const handleTimelineTimeRangeChange = (value: string) => {
    const oldValue = settings.timelineDefaultTimeRange;
    const calendarView = value.toLowerCase() as any;
    updateSettings({ timelineDefaultTimeRange: value, defaultCalendarView: calendarView });
    audit.log({
      user: user?.name ?? 'Current User',
      category: 'Users',
      action: 'Edit',
      entity: user?.email ?? 'User Settings',
      changes: [{ field: 'Default Time Range', before: oldValue ?? '', after: value }],
    });
  };

  const handleTimelineBookingTypeChange = (value: string) => {
    const oldValue = settings.timelineDefaultBookingType;
    updateSettings({ timelineDefaultBookingType: value });
    audit.log({
      user: user?.name ?? 'Current User',
      category: 'Users',
      action: 'Edit',
      entity: user?.email ?? 'User Settings',
      changes: [{ field: 'Default Booking Type', before: oldValue ?? '', after: value }],
    });
  };

  const handleTimelineBookingStatusChange = (value: string) => {
    const oldValue = settings.timelineDefaultBookingStatus;
    updateSettings({ timelineDefaultBookingStatus: value });
    audit.log({
      user: user?.name ?? 'Current User',
      category: 'Users',
      action: 'Edit',
      entity: user?.email ?? 'User Settings',
      changes: [{ field: 'Default Booking Status', before: oldValue ?? '', after: value }],
    });
  };

  const handleTimelineViewChange = (value: string) => {
    const oldValue = settings.timelineDefaultView;
    updateSettings({ timelineDefaultView: value });
    audit.log({
      user: user?.name ?? 'Current User',
      category: 'Users',
      action: 'Edit',
      entity: user?.email ?? 'User Settings',
      changes: [{ field: 'Default Timeline View', before: oldValue ?? '', after: value }],
    });
  };

  const handleTimelineTimezoneChange = async (value: string) => {
    const oldTimezone = effectiveTimezone;
    await setUserTimezone(value);
    audit.log({
      user: user?.name ?? 'Current User',
      category: 'Users',
      action: 'Edit',
      entity: user?.email ?? 'User Settings',
      changes: [{ field: 'Personal Timezone', before: oldTimezone, after: value }],
    });
    toast.success('Personal timezone updated', {
      description: 'This timezone will be used for your timeline and new bookings.',
    });
  };

  const handleResetTimezoneToDefault = async () => {
    const oldTimezone = effectiveTimezone;
    await clearUserTimezone();
    audit.log({
      user: user?.name ?? 'Current User',
      category: 'Users',
      action: 'Edit',
      entity: user?.email ?? 'User Settings',
      changes: [{ field: 'Personal Timezone', before: oldTimezone, after: `${systemDefault} (system default)` }],
    });
    toast.success('Timezone reset to system default', {
      description: `Now using system default: ${systemDefault}`,
    });
  };

  // ── User Preferences handlers ──────────────────────────────────────────────
  const handleUserConfirmDragDropToggle = (checked: boolean) => {
    updateSettings({ userConfirmDragDrop: checked });
    audit.log({
      user: user?.name ?? 'Current User',
      category: 'Users',
      action: 'Edit',
      entity: user?.email ?? 'User Settings',
      changes: [{ field: 'Confirm Drag & Drop', before: String(!checked), after: String(checked) }],
    });
  };

  const handleUserDefaultToLoggedInResourceToggle = (checked: boolean) => {
    updateSettings({ userDefaultToLoggedInResource: checked });
    audit.log({
      user: user?.name ?? 'Current User',
      category: 'Users',
      action: 'Edit',
      entity: user?.email ?? 'User Settings',
      changes: [{ field: 'Default to Logged-in Resource', before: String(!checked), after: String(checked) }],
    });
  };

  // ── Calendar Sync handlers ─────────────────────────────────────────────────
  const handleGenerateCalendarUrl = async () => {
    setIsGeneratingUrl(true);
    try {
      const response = await calendarSyncApi.generateLink({ userId: user.id });
      const url = response.icsFileUrl;
      setCalendarUrl(url && url.trim() !== '' ? url : '');
      audit.log({
        user: user?.name ?? 'Current User',
        category: 'Users',
        action: 'New',
        entity: user?.email ?? 'User Settings',
        changes: [{ field: 'Calendar Sync URL', before: null, after: 'Generated' }],
      });
      toast.success('Calendar URL generated');
    } catch (error) {
      toast.error('Failed to generate calendar URL', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    } finally {
      setIsGeneratingUrl(false);
    }
  };

  const handleCopyCalendarUrl = () => {
    if (!calendarUrl) return;
    try {
      const textarea = document.createElement('textarea');
      textarea.value = calendarUrl;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success('URL copied', { description: 'Calendar URL copied to clipboard.' });
    } catch {
      toast.error('Copy failed', { description: 'Could not copy to clipboard.' });
    }
  };

  const handleDeleteCalendarUrl = async () => {
    setIsDeletingUrl(true);
    try {
      await calendarSyncApi.deleteLink({ userId: user.id });
      setCalendarUrl('');
      setIsDeleteUrlDialogOpen(false);
      audit.log({
        user: user?.name ?? 'Current User',
        category: 'Users',
        action: 'Delete',
        entity: user?.email ?? 'User Settings',
        changes: [{ field: 'Calendar Sync URL', before: 'Active', after: null }],
      });
      toast.success('Calendar URL deleted');
    } catch (error) {
      toast.error('Failed to delete calendar URL', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    } finally {
      setIsDeletingUrl(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden">
      <div className="w-full px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
              <User className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold leading-tight">User Setup</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <DataModeToggle screen="settings" />
          </div>
        </div>

        {/* Tabbed Interface */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="w-auto inline-flex">
            <TabsTrigger value="defaults" className="gap-2">
              <Settings2 className="h-4 w-4" />
              Defaults
            </TabsTrigger>
            <TabsTrigger value="user-preferences" className="gap-2">
              <User className="h-4 w-4" />
              User Preferences
            </TabsTrigger>
            <TabsTrigger value="external-calendar" className="gap-2">
              <Globe className="h-4 w-4" />
              External Calendar Sync
            </TabsTrigger>
          </TabsList>

          {/* ── Defaults Tab ──────────────────────────────────────────────────── */}
          <TabsContent value="defaults" className="space-y-6">
            <Card className="border-primary/20">
              <CardContent className="space-y-6 pt-6">
                <div className="space-y-4">

                  {/* Time Range */}
                  <div className="space-y-2">
                    <Label htmlFor="us-timeline-time-range">Time Range</Label>
                    <Select
                      value={settings.timelineDefaultTimeRange}
                      onValueChange={handleTimelineTimeRangeChange}
                    >
                      <SelectTrigger id="us-timeline-time-range">
                        <SelectValue placeholder="Select time range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Day">Day</SelectItem>
                        <SelectItem value="Week">Week</SelectItem>
                        <SelectItem value="Month">Month</SelectItem>
                        <SelectItem value="Quarter">Quarter</SelectItem>
                        <SelectItem value="Year">Year</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Default time period displayed when opening the timeline view
                    </p>
                  </div>

                  {/* Booking Type */}
                  <div className="space-y-2">
                    <Label htmlFor="us-timeline-booking-type">Booking Type</Label>
                    <Select
                      value={settings.timelineDefaultBookingType ? String(settings.timelineDefaultBookingType) : undefined}
                      onValueChange={handleTimelineBookingTypeChange}
                    >
                      <SelectTrigger id="us-timeline-booking-type">
                        <SelectValue placeholder="Select booking type" />
                      </SelectTrigger>
                      <SelectContent>
                        {settings.bookingTypes.filter(bt => bt.active).map((bookingType) => (
                          <SelectItem key={bookingType.id} value={String(bookingType.id)}>
                            {bookingType.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Default booking type selected when creating new bookings
                    </p>
                  </div>

                  {/* Booking Status */}
                  <div className="space-y-2">
                    <Label htmlFor="us-timeline-booking-status">Booking Status</Label>
                    <Select
                      value={settings.timelineDefaultBookingStatus}
                      onValueChange={handleTimelineBookingStatusChange}
                    >
                      <SelectTrigger id="us-timeline-booking-status">
                        <SelectValue placeholder={settings.statusConfigs.filter(s => s.enabled).length === 0 ? 'No statuses configured' : 'Select booking status'} />
                      </SelectTrigger>
                      <SelectContent>
                        {settings.statusConfigs.filter(s => s.enabled).length === 0 ? (
                          <SelectItem value="none" disabled>No statuses available</SelectItem>
                        ) : (
                          settings.statusConfigs
                            .filter(s => s.enabled)
                            .map((statusConfig) => (
                              <SelectItem key={statusConfig.id} value={String(statusConfig.id)}>
                                {statusConfig.customText || statusConfig.text}
                              </SelectItem>
                            ))
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Default status applied to new bookings (e.g., Planned, Confirmed)
                    </p>
                  </div>

                  {/* Timeline View */}
                  <div className="space-y-2">
                    <Label htmlFor="us-timeline-view">Timeline View</Label>
                    <Select
                      value={normalizeTimelineView(settings.timelineDefaultView)}
                      onValueChange={handleTimelineViewChange}
                    >
                      <SelectTrigger id="us-timeline-view">
                        <SelectValue placeholder="Select timeline view" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="customer">Customer</SelectItem>
                        <SelectItem value="project-resource">Project</SelectItem>
                        <SelectItem value="resource">Resource</SelectItem>
                        <SelectItem value="role-resource">Role</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Default view mode for organising the timeline (Customer, Project, Resource, or Role)
                    </p>
                  </div>

                  {/* Timezone Default — only when feature enabled */}
                  {settings.enableTimezones && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="us-timeline-timezone">Timezone Default</Label>
                        {hasUserOverride && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleResetTimezoneToDefault}
                            className="h-7 text-xs"
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Reset to System Default
                          </Button>
                        )}
                      </div>
                      <Select
                        value={effectiveTimezone}
                        onValueChange={handleTimelineTimezoneChange}
                      >
                        <SelectTrigger id="us-timeline-timezone">
                          <SelectValue placeholder="Select timezone" />
                        </SelectTrigger>
                        <SelectContent>
                          {timezoneOptions
                            .filter((tz) => settings.availableTimezones.includes(tz.value))
                            .map((tz) => (
                              <SelectItem key={tz.value} value={tz.value}>
                                {tz.label}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">
                          {hasUserOverride ? (
                            <>
                              <span className="font-medium text-primary">Personal override active.</span> Your timezone will be used for timeline and new bookings. System default: {TIMEZONES.find(tz => tz.value === systemDefault)?.label || systemDefault}
                            </>
                          ) : (
                            <>Using system default timezone set by administrator. This will be used for your timeline and new bookings.</>
                          )}
                        </p>
                        {currentMode === 'demo' && (
                          <p className="text-xs text-amber-600 font-medium">
                            ⚠ Demo Mode: Changes won't persist. Switch to Live mode in the top-right corner.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── User Preferences Tab ──────────────────────────────────────────── */}
          <TabsContent value="user-preferences" className="space-y-6">
            <Card className="border-primary/20">
              <CardContent className="space-y-6 pt-6">
                <div className="space-y-4">

                  {/* Confirm Drag/Drop */}
                  <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                    <div className="flex-1 min-w-0">
                      <Label htmlFor="us-user-confirm-drag-drop" className="text-sm font-medium cursor-pointer">Confirm Drag/Drop</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Require confirmation when dragging and dropping bookings (personal preference)
                      </p>
                    </div>
                    <Checkbox
                      id="us-user-confirm-drag-drop"
                      checked={settings.userConfirmDragDrop ?? settings.confirmDragDrop}
                      onCheckedChange={(v) => handleUserConfirmDragDropToggle(!!v)}
                    />
                  </div>

                  {/* Default to Logged In Resource */}
                  <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                    <div className="flex-1 min-w-0">
                      <Label htmlFor="us-user-default-logged-in" className="text-sm font-medium cursor-pointer">Default to Logged In Resource</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Automatically select yourself as the default resource (personal preference)
                      </p>
                    </div>
                    <Checkbox
                      id="us-user-default-logged-in"
                      checked={settings.userDefaultToLoggedInResource ?? settings.defaultToLoggedInResource}
                      onCheckedChange={(v) => handleUserDefaultToLoggedInResourceToggle(!!v)}
                    />
                  </div>

                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── External Calendar Sync Tab ─────────────────────────────────────── */}
          <TabsContent value="external-calendar" className="space-y-6">
            <Card className="border-primary/20">
              <CardContent className="space-y-6 pt-6">

                <p className="text-sm text-muted-foreground">
                  Generate a calendar feed URL to sync your scheduler bookings with an external calendar application.
                </p>

                {/* Generate + URL row */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Calendar URL</Label>
                  <div className="flex gap-2">
                    <Button
                      className="bg-teal-600 hover:bg-teal-700 text-white shrink-0"
                      onClick={handleGenerateCalendarUrl}
                      disabled={isGeneratingUrl}
                    >
                      {isGeneratingUrl ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Link className="h-4 w-4 mr-2" />
                      )}
                      {isGeneratingUrl ? 'Generating…' : 'Generate URL'}
                    </Button>
                    <Input
                      readOnly
                      value={calendarUrl}
                      placeholder={isLoadingCalendarUrl ? 'Loading…' : 'Please generate URL'}
                      className="flex-1 bg-muted text-muted-foreground text-sm font-mono"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopyCalendarUrl}
                      disabled={!calendarUrl}
                      title="Copy URL"
                      className="shrink-0"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>

                  {calendarUrl && (
                    <div className="flex items-center justify-between rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
                      <p className="text-xs text-muted-foreground">
                        Deleting this URL will revoke access. You can generate a new one at any time.
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0 ml-3 gap-1.5"
                        onClick={() => setIsDeleteUrlDialogOpen(true)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete URL
                      </Button>
                    </div>
                  )}
                </div>

                {/* How-to instructions */}
                <div className="space-y-2 rounded-md bg-muted/50 p-4">
                  <p className="text-sm font-medium">How to add to your calendar:</p>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                    <li>Generate the URL above</li>
                    <li>Copy the URL</li>
                    <li>Open your calendar tool</li>
                    <li>Go to the option "Add Internet/Web Calendar"</li>
                    <li>Paste the copied URL when prompted</li>
                    <li>Save</li>
                  </ol>
                </div>

                <p className="text-xs text-muted-foreground border-t pt-3">
                  <strong>Note:</strong> Calendar events will start appearing as new bookings are created on your timeline in Scheduler and on the next refresh cycle of your calendar client, which may be several minutes. Send/Receive in Outlook should start a refresh or you can re-add the calendar to see the latest events immediately.
                </p>

              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </div>

      {/* Delete Calendar URL Confirmation Dialog */}
      <AlertDialog open={isDeleteUrlDialogOpen} onOpenChange={setIsDeleteUrlDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Calendar URL
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the calendar feed URL?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCalendarUrl}
              disabled={isDeletingUrl}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingUrl ? 'Deleting…' : 'Yes'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
